import React, { useState, useEffect } from 'react';
import Icon from './ui/Icon';
import API from '../services/api';
import { getUser } from '../utils/authUtil';

const DiagnosticWizard = ({ testId, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [batches, setBatches] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [students, setStudents] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedCollegeId, setSelectedCollegeId] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [topicConfigs, setTopicConfigs] = useState({}); // { topicId: { easy: 0, medium: 0, hard: 0 } }

  // Filter state
  const [filters, setFilters] = useState({
    branch: '',
    arrears: '',
    cgpa: ''
  });

  // Get current user info
  const user = getUser();
  const isSuperAdmin = user?.realRole === 'super_admin';

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      let collegeList = [];
      let studentList = [];
      let batchList = [];

      // If super admin, fetch all colleges
      if (isSuperAdmin) {
        const collegeRes = await API.get('/colleges');
        collegeList = collegeRes.data || [];
      }

      const [studentRes, topicRes, batchRes] = await Promise.all([
        API.get('/students'),
        API.get('/questions/topics'),
        API.get('/batches')
      ]);
      
      studentList = studentRes.data || [];
      batchList = batchRes.data || [];

      if (isSuperAdmin) {
        setColleges(collegeList);
      }
      setStudents(studentList);
      setBatches(batchList);
      setTopics(topicRes.data);

      // Initialize topic configs with 0s
      const initial = {};
      topicRes.data.forEach(t => {
        initial[t.id] = { easy: 0, medium: 0, hard: 0 };
      });
      setTopicConfigs(initial);
    } catch (err) {
      console.error('Failed to fetch wizard data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (sid) => {
    setSelectedStudentIds(prev =>
      prev.includes(sid) ? prev.filter(id => id !== sid) : [...prev, sid]
    );
  };

  const handleConfigChange = (topicId, field, value, max) => {
    const n = Math.max(0, Math.min(parseInt(value) || 0, max ?? Infinity));
    setTopicConfigs(prev => ({
      ...prev,
      [topicId]: { ...prev[topicId], [field]: n }
    }));
  };

  const handleAssign = async () => {
    setLoading(true);
    try {
      // Format configs for backend
      const formattedConfigs = Object.entries(topicConfigs)
        .filter(([_, cfg]) => cfg.easy > 0 || cfg.medium > 0 || cfg.hard > 0)
        .map(([tid, cfg]) => ({
          topic_id: tid,
          easy_count: cfg.easy,
          medium_count: cfg.medium,
          hard_count: cfg.hard
        }));

      if (formattedConfigs.length === 0) {
        alert('Please select at least one question from any topic.');
        setLoading(false);
        return;
      }

      await API.post(`/tests/${testId}/assign-diagnostic`, {
        student_ids: selectedStudentIds.length > 0 ? selectedStudentIds : undefined,
        batch_id: selectedBatchId || undefined,
        college_id: selectedCollegeId || undefined,
        topic_configs: formattedConfigs
      });

      onComplete();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign diagnostic test');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStudents = () => {
    let filtered = students;

    // If super admin selected a college, filter students to that college
    if (isSuperAdmin && selectedCollegeId) {
      const college = colleges.find(c => c.id === selectedCollegeId);
      if (college) {
        filtered = filtered.filter(s => s.college_id === selectedCollegeId || s.college?.id === selectedCollegeId);
      }
    }

    // Apply additional filters
    return filtered.filter(s => {
      if (filters.branch && s.branch !== filters.branch) return false;
      if (filters.arrears !== '' && s.arrears > parseInt(filters.arrears)) return false;
      if (filters.cgpa !== '' && s.cgpa < parseFloat(filters.cgpa)) return false;
      return true;
    });
  };

  if (loading && step === 1) return <div className="loading">Loading Wizard...</div>;

  return (
    <div className="diag-wizard">
      <div className="wizard-stepper">
        <div className={`step-item ${step === 1 ? 'active' : ''}`}>1. Select Students</div>
        <div className={`step-item ${step === 2 ? 'active' : ''}`}>2. Configure Topics</div>
        <div className={`step-item ${step === 3 ? 'active' : ''}`}>3. Finalize</div>
      </div>

      <div className="wizard-content">
        {step === 1 && (
          <div className="wizard-step-1">
            <h4>Who should take this test?</h4>
            
            {isSuperAdmin && (
              <div className="fg" style={{ marginBottom: 20, background: 'var(--z-card-alt)', padding: 15, borderRadius: 10, border: '1px solid #6d28d9' }}>
                <label style={{ fontWeight: 700, color: '#6d28d9', marginBottom: 10 }}>Select College (Super Admin)</label>
                <select value={selectedCollegeId} onChange={e => setSelectedCollegeId(e.target.value)} 
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #6d28d9', fontSize: 14 }}>
                  <option value="">-- Choose College --</option>
                  {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                  {selectedCollegeId ? `Showing students from selected college` : 'Select a college to assign diagnostic test to all its students'}
                </p>
              </div>
            )}
            
            <div className="wizard-filters">
              <div className="filter-group">
                <label>Branch</label>
                <select value={filters.branch} onChange={e => setFilters({...filters, branch: e.target.value})}>
                  <option value="">All Branches</option>
                  {[...new Set(getFilteredStudents().map(s => s.branch))].filter(Boolean).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Max Arrears</label>
                <input type="number" placeholder="Eg: 0" value={filters.arrears} 
                  onChange={e => setFilters({...filters, arrears: e.target.value})} />
              </div>
              <div className="filter-group">
                <label>Min CGPA</label>
                <input type="number" step="0.1" placeholder="Eg: 7.5" value={filters.cgpa}
                  onChange={e => setFilters({...filters, cgpa: e.target.value})} />
              </div>
            </div>

            <div className="fg" style={{ marginBottom: 20 }}>
              <label>Select Batch (Optional)</label>
              <select value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}>
                <option value="">-- Choose Batch --</option>
                {batches
                  .filter(b => !filters.branch || b.batch_name.toLowerCase().includes(filters.branch.toLowerCase()))
                  .map(b => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
              </select>
            </div>
            
            <div className="student-picker-mini">
              <label>Individual Students ({selectedStudentIds.length} selected)</label>
              <div className="sp-list">
                {getFilteredStudents()
                  .map(s => (
                    <div key={s.id} 
                      className={`sp-item ${selectedStudentIds.includes(s.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleStudent(s.id)}>
                      {s.name} ({s.roll_number})
                      <div className="sp-meta-small">Arrears: {s.arrears} | CGPA: {s.cgpa}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-step-2">
            <h4>Question Breakdown per Topic</h4>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 15 }}>
              Enter the number of questions to pick for each topic.
            </p>
            <div className="topic-grid">
              <div className="tg-header">
                <span>Topic Name</span>
                <span>Easy (Avail)</span>
                <span>Medium (Avail)</span>
                <span>Hard (Avail)</span>
              </div>
              {topics.map(t => (
                <div key={t.id} className="tg-row">
                  <div className="tg-name-container">
                    <span className="tg-name">{t.name}</span>
                    <span className="avail-badge-total">Total: {t.availability.total}</span>
                  </div>
                  
                  <div className="tg-input-wrap">
                    <input type="number" min="0" max={t.availability.easy}
                      value={topicConfigs[t.id]?.easy || 0}
                      onChange={e => handleConfigChange(t.id, 'easy', e.target.value, t.availability.easy)} />
                    <span className="avail-limit">/ {t.availability.easy}</span>
                  </div>

                  <div className="tg-input-wrap">
                    <input type="number" min="0" max={t.availability.medium}
                      value={topicConfigs[t.id]?.medium || 0}
                      onChange={e => handleConfigChange(t.id, 'medium', e.target.value, t.availability.medium)} />
                    <span className="avail-limit">/ {t.availability.medium}</span>
                  </div>

                  <div className="tg-input-wrap">
                    <input type="number" min="0" max={t.availability.hard}
                      value={topicConfigs[t.id]?.hard || 0}
                      onChange={e => handleConfigChange(t.id, 'hard', e.target.value, t.availability.hard)} />
                    <span className="avail-limit">/ {t.availability.hard}</span>
                  </div>
                </div>
              ))}
              {topics.length === 0 && (
                <div className="empty-topics-msg">
                  <Icon name="warning" size={16} /> No topics found with questions available for your college.
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="wizard-step-3">
            <h4>Assignment Summary</h4>
            <div className="summary-box">
              <div className="sum-item">
                <strong>Audience:</strong> 
                {selectedCollegeId ? ` All students from ${colleges.find(c => c.id === selectedCollegeId)?.name || 'selected college'}` : ''}
                {selectedBatchId ? ` Batch: ${batches.find(b => b.id === selectedBatchId)?.batch_name || 'selected batch'}` : ''}
                {selectedStudentIds.length > 0 ? ` ${selectedStudentIds.length} Selected Students` : ''}
              </div>
              <div className="sum-item">
                <strong>Total Questions:</strong> {Object.values(topicConfigs).reduce((a, b) => a + b.easy + b.medium + b.hard, 0)}
              </div>
              <div className="sum-details">
                {Object.entries(topicConfigs).map(([tid, cfg]) => {
                   if (cfg.easy === 0 && cfg.medium === 0 && cfg.hard === 0) return null;
                   const tName = topics.find(t => t.id === tid)?.name;
                   return <div key={tid}>{tName}: {cfg.easy}E, {cfg.medium}M, {cfg.hard}H</div>
                })}
              </div>
            </div>
            <p style={{ marginTop: 20, fontSize: 13, color: '#dc2626' }}>
              <Icon name="warning" size={16} /> This will create personalized question sets for all selected students.
            </p>
          </div>
        )}
      </div>

      <div className="wizard-actions">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <div style={{ marginLeft: 'auto' }}>
          {step > 1 && <button className="btn-prev" onClick={() => setStep(step - 1)}>Back</button>}
          {step < 3 ? (
            <button className="btn-next" 
              disabled={(step === 1 && !selectedBatchId && !selectedCollegeId && selectedStudentIds.length === 0)}
              onClick={() => setStep(step + 1)}>
              Next <Icon name="arrow-right" size={16} />
            </button>
          ) : (
            <button className="btn-assign-final" onClick={handleAssign} disabled={loading}>
              {loading ? 'Processing...' : 'Finish & Assign Test'}
            </button>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .diag-wizard { display: flex; flex-direction: column; min-height: 400px; }
        .wizard-stepper { display: flex; gap: 20px; border-bottom: 2px solid var(--z-border); padding-bottom: 15px; margin-bottom: 20px; }
        .step-item { font-size: 13px; font-weight: 600; color: var(--z-muted-2); }
        .step-item.active { color: var(--z-violet); border-bottom: 2px solid var(--z-violet); margin-bottom: -17px; padding-bottom: 15px; }
        .wizard-content { flex: 1; min-height: 300px; }
        
        .wizard-filters { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 20px; background: var(--z-card-alt); padding: 15px; border-radius: 10px; border: 1px solid var(--z-border); }
        .filter-group { display: flex; flex-direction: column; gap: 5px; }
        .filter-group label { font-size: 11px; font-weight: 700; color: var(--z-muted); text-transform: uppercase; }
        .filter-group select, .filter-group input { padding: 8px; border: 1px solid var(--z-border); border-radius: 6px; font-size: 13px; }

        .topic-grid { display: flex; flex-direction: column; gap: 8px; max-height: 400px; overflow-y: auto; padding-right: 10px; }
        .tg-header, .tg-row { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 10px; align-items: center; padding: 10px; border-radius: 8px; }
        .tg-header { background: var(--z-card-alt); font-weight: 700; font-size: 12px; text-transform: uppercase; color: var(--z-muted); }
        .tg-row { background: white; border: 1px solid var(--z-border); }
        .tg-name { font-size: 14px; font-weight: 600; color: var(--z-ink-2); }
        .tg-row input { padding: 6px; border: 1px solid var(--z-border); border-radius: 4px; text-align: center; }
        .sp-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; max-height: 200px; overflow-y: auto; margin-top: 10px; border: 1px solid var(--z-border); padding: 10px; border-radius: 8px; }
        .sp-item { padding: 8px; background: var(--z-card-alt); border-radius: 6px; font-size: 12px; cursor: pointer; transition: all 0.2s; }
        .sp-item.selected { background: var(--z-violet); color: white; }
        .sp-meta-small { font-size: 10px; opacity: 0.7; margin-top: 2px; }
        .tg-name-container { display: flex; flex-direction: column; gap: 2px; }
        .avail-badge-total { font-size: 10px; color: var(--z-violet); font-weight: 700; background: var(--z-tint-violet); padding: 2px 6px; border-radius: 4px; width: fit-content; }
        .tg-input-wrap { display: flex; align-items: center; gap: 5px; }
        .tg-input-wrap input { width: 50px; }
        .avail-limit { font-size: 11px; color: var(--z-muted); font-weight: 500; }
        .empty-topics-msg { padding: 40px; text-align: center; color: var(--z-danger); font-weight: 700; background: var(--z-danger-tint); border-radius: 12px; border: 1px dashed var(--z-danger); margin-top: 20px; }
        .summary-box { background: var(--z-tint-violet); border: 1.5px solid var(--z-violet); border-radius: 12px; padding: 20px; margin-top: 10px; }
        .sum-item { margin-bottom: 10px; font-size: 14px; }
        .sum-details { font-size: 12px; color: var(--z-muted); display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin-top: 10px; border-top: 1px solid rgba(106, 48, 176, 0.2); padding-top: 10px; }
        .wizard-actions { display: flex; padding-top: 20px; border-top: 1px solid var(--z-border); margin-top: 20px; }
        .btn-prev { background: var(--z-surface-sunken); border: none; padding: 10px 20px; border-radius: 8px; cursor: pointer; margin-right: 10px; }
        .btn-next, .btn-assign-final { background: var(--z-violet); color: white; border: none; padding: 10px 25px; border-radius: 8px; cursor: pointer; font-weight: 600; }
        .btn-next:disabled { background: var(--z-line); cursor: not-allowed; }
      `}} />
    </div>
  );
};

export default DiagnosticWizard;
